import React, { useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Field, useFormState } from 'react-final-form';
import { useQuery } from 'react-query';
import { useOkapiKy } from '@folio/stripes/core';
import stripesFinalForm from '@folio/stripes/final-form';
import {
  Button,
  Col,
  Pane,
  PaneFooter,
  RadioButtonGroup,
  RadioButton,
  Row,
  Select,
  TextField,
  Callout,
  Checkbox,
} from '@folio/stripes/components';
import { Label, Paneset } from '@folio/stripes-components';

const STORAGE_TYPE_KEY = 'STORAGE_TYPE';
const MAX_SIZE_KEY = 'MAX_UPLOAD_FILE_SIZE';
const MAX_SIZE_UNIT_KEY = 'MAX_UPLOAD_FILE_SIZE_UNIT';
const S3_URL_KEY = 'S3_URL';
const S3_REGION_KEY = 'S3_REGION';
const S3_BUCKET_KEY = 'S3_BUCKET';
const S3_ACCESS_KEY_ID_KEY = 'S3_ACCESS_KEY_ID';
const S3_SECRET_ACCESS_KEY_KEY = 'S3_SECRET_ACCESS_KEY';
const S3_IS_AWS_KEY = 'S3_IS_AWS';
const SENSITIVE_PLACEHOLDER = '••••••••';

const STORAGE_TYPES = ['LOCAL', 'MINIO', 'AWS_S3'];
const SIZE_UNITS = ['KB', 'MB', 'GB'];

const validate = (values) => {
  const errors = {};
  if (!values.maxSize || isNaN(values.maxSize) || Number(values.maxSize) <= 0) {
    errors.maxSize = <FormattedMessage id="ui-data-export.settings.storage.sizeError" />;
  }
  return errors;
};

const S3Fields = () => (
  <>
    <Row>
      <Col xs={6}>
        <Field
          name="s3Url"
          label={<FormattedMessage id="ui-data-export.settings.storage.s3Url" />}
          component={TextField}
        />
      </Col>
      <Col xs={6}>
        <Field
          name="s3Region"
          label={<FormattedMessage id="ui-data-export.settings.storage.s3Region" />}
          component={TextField}
        />
      </Col>
    </Row>
    <Row>
      <Col xs={6}>
        <Field
          name="s3Bucket"
          label={<FormattedMessage id="ui-data-export.settings.storage.s3Bucket" />}
          component={TextField}
        />
      </Col>
      <Col xs={6}>
        <Field
          name="s3AccessKeyId"
          label={<FormattedMessage id="ui-data-export.settings.storage.s3AccessKeyId" />}
          component={TextField}
          placeholder={SENSITIVE_PLACEHOLDER}
          type="password"
          autoComplete="new-password"
        />
      </Col>
    </Row>
    <Row>
      <Col xs={6}>
        <Field
          name="s3SecretAccessKey"
          label={<FormattedMessage id="ui-data-export.settings.storage.s3SecretAccessKey" />}
          component={TextField}
          placeholder={SENSITIVE_PLACEHOLDER}
          type="password"
          autoComplete="new-password"
        />
      </Col>
      <Col xs={6}>
        <Field name="s3IsAws" type="checkbox">
          {({ input }) => (
            <Checkbox
              label={<FormattedMessage id="ui-data-export.settings.storage.s3IsAws" />}
              checked={input.checked}
              onChange={input.onChange}
            />
          )}
        </Field>
      </Col>
    </Row>
  </>
);

const StorageSettingsForm = ({ handleSubmit, pristine, submitting, form }) => {
  const intl = useIntl();
  const calloutRef = useRef(null);
  const { values } = useFormState({ subscription: { values: true } });

  const footer = (
    <PaneFooter
      renderEnd={
        <Button type="submit" buttonStyle="primary" disabled={pristine || submitting} marginBottom0>
          <FormattedMessage id="ui-data-export.settings.storage.save" />
        </Button>
      }
    />
  );

  return (
    <Paneset>
      <form
        style={{ height: '100%', width: '100%' }}
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            await handleSubmit(e);
            form.reset(form.getState().values);
            calloutRef.current?.sendCallout({
              message: <FormattedMessage id="ui-data-export.settings.storage.saveSuccess" />,
            });
          } catch {
            calloutRef.current?.sendCallout({
              type: 'error',
              message: <FormattedMessage id="ui-data-export.settings.storage.saveError" />,
            });
          }
        }}
      >
        <Pane
          defaultWidth="fill"
          fluidContentWidth
          paneTitle={<FormattedMessage id="ui-data-export.settings.storage.title" />}
          footer={footer}
        >
          <Field name="storageType">
            {({ input }) => (
              <RadioButtonGroup
                label={
                  <Label>
                    <FormattedMessage id="ui-data-export.settings.storage.providerLabel" />
                  </Label>
                }
                value={input.value}
                onChange={input.onChange}
              >
                {STORAGE_TYPES.map((type) => (
                  <RadioButton
                    key={type}
                    value={type}
                    label={intl.formatMessage({ id: `ui-data-export.settings.storage.${type.toLowerCase()}` })}
                  />
                ))}
              </RadioButtonGroup>
            )}
          </Field>

          {values.storageType !== 'LOCAL' && <S3Fields />}

          <hr />
          <Row>
            <Col xs={6}>
              <Field
                name="maxSize"
                label={<FormattedMessage id="ui-data-export.settings.storage.maxFileSizeLabel" />}
                component={TextField}
                type="number"
                min="1"
              />
            </Col>
            <Col xs={6}>
              <Field
                name="maxSizeUnit"
                label={<FormattedMessage id="ui-data-export.settings.storage.maxFileSizeUnit" />}
                component={Select}
              >
                {SIZE_UNITS.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </Field>
            </Col>
          </Row>
        </Pane>
      </form>

      <Callout ref={calloutRef} />
    </Paneset>
  );
};

const StorageSettingsFormWrapped = stripesFinalForm({
  validate,
  navigationCheck: true,
  subscription: { values: true, pristine: true, submitting: true },
})(StorageSettingsForm);

export const StorageSettings = () => {
  const ky = useOkapiKy();
  const fetchConfig = (key) => ky(`data-export/configuration/${key}`).json();

  const { data: storageTypeData } = useQuery(
    ['data-export', 'config', STORAGE_TYPE_KEY],
    () => fetchConfig(STORAGE_TYPE_KEY),
    { retry: false }
  );
  const { data: maxSizeData } = useQuery(['data-export', 'config', MAX_SIZE_KEY], () => fetchConfig(MAX_SIZE_KEY), {
    retry: false,
  });
  const { data: maxSizeUnitData } = useQuery(
    ['data-export', 'config', MAX_SIZE_UNIT_KEY],
    () => fetchConfig(MAX_SIZE_UNIT_KEY),
    { retry: false }
  );
  const { data: s3UrlData } = useQuery(['data-export', 'config', S3_URL_KEY], () => fetchConfig(S3_URL_KEY), {
    retry: false,
  });
  const { data: s3RegionData } = useQuery(['data-export', 'config', S3_REGION_KEY], () => fetchConfig(S3_REGION_KEY), {
    retry: false,
  });
  const { data: s3BucketData } = useQuery(['data-export', 'config', S3_BUCKET_KEY], () => fetchConfig(S3_BUCKET_KEY), {
    retry: false,
  });
  const { data: s3IsAwsData } = useQuery(['data-export', 'config', S3_IS_AWS_KEY], () => fetchConfig(S3_IS_AWS_KEY), {
    retry: false,
  });

  const initialValues = {
    storageType: storageTypeData?.value || 'LOCAL',
    maxSize: maxSizeData?.value || '15',
    maxSizeUnit: maxSizeUnitData?.value || 'MB',
    s3Url: s3UrlData?.value || '',
    s3Region: s3RegionData?.value || '',
    s3Bucket: s3BucketData?.value || '',
    s3AccessKeyId: '',
    s3SecretAccessKey: '',
    s3IsAws: s3IsAwsData?.value === 'true',
  };

  const handleSubmit = async (values) => {
    await Promise.all([
      ky.post('data-export/configuration', { json: { key: STORAGE_TYPE_KEY, value: values.storageType } }).json(),
      ky.post('data-export/configuration', { json: { key: MAX_SIZE_KEY, value: values.maxSize } }).json(),
      ky.post('data-export/configuration', { json: { key: MAX_SIZE_UNIT_KEY, value: values.maxSizeUnit } }).json(),
      ky.post('data-export/configuration', { json: { key: S3_URL_KEY, value: values.s3Url } }).json(),
      ky.post('data-export/configuration', { json: { key: S3_REGION_KEY, value: values.s3Region } }).json(),
      ky.post('data-export/configuration', { json: { key: S3_BUCKET_KEY, value: values.s3Bucket } }).json(),
      ky.post('data-export/configuration', { json: { key: S3_IS_AWS_KEY, value: String(values.s3IsAws) } }).json(),
      ...(values.s3AccessKeyId
        ? [
            ky
              .post('data-export/configuration', { json: { key: S3_ACCESS_KEY_ID_KEY, value: values.s3AccessKeyId } })
              .json(),
          ]
        : []),
      ...(values.s3SecretAccessKey
        ? [
            ky
              .post('data-export/configuration', {
                json: { key: S3_SECRET_ACCESS_KEY_KEY, value: values.s3SecretAccessKey },
              })
              .json(),
          ]
        : []),
    ]);
    await ky.post('data-export/configuration/refresh');
  };

  return <StorageSettingsFormWrapped initialValues={initialValues} onSubmit={handleSubmit} />;
};
